import { formatUnknownDatePickerValue } from 'components/molecules/PPA/commons/formControl';
import type { SimulationTableColumnMeta, SimulationTableConfig } from 'types/ppaSimulationMeta';
import type {
    SimulationKpiKind,
    SimulationTableApiRow,
    SimulationTableKpiItem,
    SimulationVisibleAttributeColumn,
    SimulationVisibleKpiColumn,
} from './simulationTable.helpersTypes';
import {
    columnType,
    findSimulationTableColumn,
    normalizeSimulationAttributeKey,
    resolveSimulationKpiKind,
} from './simulationTable.helpersVisibility';

function formatKpiDisplayValue(kpi: SimulationTableKpiItem | undefined): string {
    if (!kpi) return '';
    if (kpi.value !== undefined && kpi.value !== null && kpi.value !== '') {
        return String(kpi.value);
    }
    if (typeof kpi.raw_value === 'number' && Number.isFinite(kpi.raw_value)) {
        return String(kpi.raw_value);
    }
    return '';
}

function formatImplementFromValue(value: unknown): string {
    return formatUnknownDatePickerValue(value);
}

function findMatchingAttributeColumn(
    attributeType: string,
    visibleAttributeColumns: SimulationVisibleAttributeColumn[],
): SimulationVisibleAttributeColumn | undefined {
    const normalizedType = normalizeSimulationAttributeKey(attributeType);
    if (!normalizedType) return undefined;
    return visibleAttributeColumns.find(
        (column) => normalizeSimulationAttributeKey(column.attributeKey) === normalizedType,
    );
}

function findMatchingKpiColumn(
    kpiId: string,
    visibleKpiColumns: SimulationVisibleKpiColumn[],
): SimulationVisibleKpiColumn | undefined {
    const normalizedId = normalizeSimulationAttributeKey(kpiId);
    if (!normalizedId) return undefined;
    return visibleKpiColumns.find((column) => normalizeSimulationAttributeKey(column.kpiId) === normalizedId);
}

function resolveKpiRowKey(
    kpi: SimulationTableKpiItem,
    visibleColumns: SimulationVisibleKpiColumn[],
    fallbackColumnId: string | undefined,
    defaultKey: string,
): string {
    return (
        findMatchingKpiColumn(kpi.kpi_id ?? '', visibleColumns)?.kpiId ||
        (visibleColumns.length > 0 ? kpi.kpi_id : fallbackColumnId) ||
        kpi.kpi_id ||
        defaultKey
    );
}

function applyInputKpiValues(
    mapped: Record<string, unknown>,
    kpi: SimulationTableKpiItem,
    groupKey: string,
    metrics: SimulationTableColumnMeta[],
): string {
    const valuePrefix = kpi.valuePrefix || '';
    if (metrics.length === 0) {
        mapped[`${groupKey}_current`] = formatKpiDisplayValue(kpi);
        mapped[`${groupKey}_chg`] = kpi.change !== undefined && kpi.change !== null ? String(kpi.change) : '';
        mapped[`${groupKey}_new`] =
            kpi.updated_value !== undefined && kpi.updated_value !== null ? String(kpi.updated_value) : '';
        return valuePrefix;
    }

    metrics.forEach((metric) => {
        const dataIndex = `${groupKey}_${metric.id}`;
        if (metric.id === 'chg') {
            mapped[dataIndex] = kpi.change !== undefined && kpi.change !== null ? String(kpi.change) : '';
            return;
        }
        if (metric.id === 'new') {
            mapped[dataIndex] =
                kpi.updated_value !== undefined && kpi.updated_value !== null ? String(kpi.updated_value) : '';
            return;
        }
        mapped[dataIndex] = formatKpiDisplayValue(kpi);
    });
    return valuePrefix;
}

export function mapSimulationTableRows(
    rows: SimulationTableApiRow[],
    tableConfig: SimulationTableConfig,
    visibleAttributeColumns: SimulationVisibleAttributeColumn[] = [],
    visibleKpiColumns: Record<SimulationKpiKind, SimulationVisibleKpiColumn[]> = {
        display: [],
        input: [],
        date: [],
        checkbox: [],
    },
): Record<string, unknown>[] {
    const columns = tableConfig.columns;
    const attributeTemplate = findSimulationTableColumn(
        columns,
        (column) => column.isAttribute === true || columnType(column) === 'attribute',
    );
    const aiColumn = findSimulationTableColumn(columns, (column) => column.id === 'ai');
    const kpiColumn = findSimulationTableColumn(
        columns,
        (column) => column.id === 'kpi_id' || (columnType(column) === 'kpi' && column.id !== 'ai'),
    );
    const userInputGroup = findSimulationTableColumn(
        columns,
        (column) => column.id === 'user_input_kpi' || columnType(column) === 'table_columns',
    );
    const implementFromColumn = findSimulationTableColumn(
        columns,
        (column) => column.id === 'implement_from' || columnType(column) === 'date_picker',
    );
    const competitorColumn = findSimulationTableColumn(
        columns,
        (column) =>
            column.id === 'competitor_follows' ||
            (columnType(column) === 'checkbox' && column.id !== 'select'),
    );

    return rows.map((row, rowIndex) => {
        const mapped: Record<string, unknown> = {
            key: `simulation-row-${rowIndex}`,
            isParent: false,
        };

        visibleAttributeColumns.forEach((column) => {
            mapped[column.attributeKey] = '';
        });

        (row.hierarchy ?? []).forEach((item) => {
            const attributeType = item.attribute_type?.trim();
            if (!attributeType) return;
            const cellValue = item.description?.trim() || item.ex_id || '';
            const matchedColumn = findMatchingAttributeColumn(attributeType, visibleAttributeColumns);
            mapped[matchedColumn?.attributeKey ?? attributeType] = cellValue;
        });

        if (visibleAttributeColumns.length === 0 && attributeTemplate) {
            const firstHierarchy = row.hierarchy?.[0];
            mapped[attributeTemplate.id] = firstHierarchy?.description?.trim() || firstHierarchy?.ex_id || '';
        }
        if (aiColumn) {
            mapped[aiColumn.id] = '';
        }

        let valuePrefix = '';
        (row.kpi ?? []).forEach((kpi) => {
            const kind = resolveSimulationKpiKind(kpi);

            if (kind === 'display') {
                mapped[resolveKpiRowKey(kpi, visibleKpiColumns.display, kpiColumn?.id, 'kpi_id')] =
                    formatKpiDisplayValue(kpi);
                return;
            }

            if (kind === 'input') {
                valuePrefix =
                    applyInputKpiValues(
                        mapped,
                        kpi,
                        resolveKpiRowKey(kpi, visibleKpiColumns.input, userInputGroup?.id, 'user_input_kpi'),
                        userInputGroup?.children ?? [],
                    ) || valuePrefix;
                return;
            }

            if (kind === 'date') {
                mapped[resolveKpiRowKey(kpi, visibleKpiColumns.date, implementFromColumn?.id, 'implement_from')] =
                    formatImplementFromValue(
                        kpi.value !== undefined && kpi.value !== null && kpi.value !== ''
                            ? kpi.value
                            : kpi.raw_value,
                    );
                return;
            }

            mapped[resolveKpiRowKey(kpi, visibleKpiColumns.checkbox, competitorColumn?.id, 'competitor_follows')] =
                kpi.value === true || kpi.value === 'true';
        });

        mapped.valuePrefix = valuePrefix;
        return mapped;
    });
}
